import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../components/ui";
import FormInput from "../../components/ui/FormInput";
import {
  nomineeDetailsFields,
  handleFileChange,
  memberNomineeDetailsFields,
} from "../../utils/utils";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { memberNomineeDetailsValidationSchema } from "../../utils/validationSchema";
import ErrorMessage from "../../components/shared/ErrorMessage";

const NomineeDetails = ({
  customerId,
  nomineeDetailsData,
  setNomineeDetailsData,
  handleNext,
  type,
  locked = false,
  mode,
}) => {
  const navigate = useNavigate();
  const [isEditingRaw, setIsEditingRaw] = useState(type === "Edit");
  // `locked` wins over this component's own isEditing. Passing type="View" is
  // NOT enough: the Edit button below renders *because* the type is View, and
  // clicking it sets isEditing = true, which would reopen a disbursed
  // application. Defaults to false so the Customers flow is unaffected.
  const isEditing = locked ? false : isEditingRaw;
  const setIsEditing = (v) => { if (!locked) setIsEditingRaw(v); };
  const [errorMsg, setErrorMsg] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    resolver:
      mode === "businessLoan"
        ? yupResolver(memberNomineeDetailsValidationSchema)
        : undefined,
    defaultValues: nomineeDetailsData,
  });

  const [filePreviews, setFilePreviews] = useState({});
  const [existingFiles, setExistingFiles] = useState({});

  useEffect(() => {
    if (nomineeDetailsData) {
      reset(nomineeDetailsData); // Reset form with new data
      Object.keys(nomineeDetailsData).forEach((key) => {
        setValue(key, nomineeDetailsData[key]);
        if (key.endsWith("Image") && nomineeDetailsData[key]) {
          setExistingFiles((prev) => ({
            ...prev,
            [key]: `${API_BASE_URL}/images/${nomineeDetailsData[key]}`,
          }));
        }
      });
    }
  }, [nomineeDetailsData, setValue, reset]);

  const onSubmit = async (data) => {
    const formData = new FormData();

    // Insert or update is decided by WHETHER THIS SECTION EXISTS, not by the
    // URL's `type` (nor by isEditing, which is `type === "Edit"` renamed).
    // Nominee Details is optional, so a customer being edited may never have had
    // a row — sending type:"Edit" with no id made the server answer "Update
    // requires a valid ID." for 12,776 of 13,625 customers on the live database.
    const existingId = nomineeDetailsData?.id;
    const isNew = !existingId;

    if (isNew) {
      // Required, or the new row is orphaned with no customer.
      if (!/^\d+$/.test(customerId)) {
        console.error("Invalid customerId:", customerId);
        throw new Error("Invalid customerId value.");
      }
      formData.append("customerId", customerId);
    }


    // `id` is excluded alongside customerId: FormData.append does not replace,
    // it adds, so a stale id carried in the form values would arrive as a second
    // field competing with the authoritative one appended below.
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "customerId" && key !== "id" && value !== null && value !== "") {
        if (filePreviews[key]?.file) {
          formData.append(key, filePreviews[key].file);
        } else {
          formData.append(key, value);
        }
      }
    });

    formData.append("model", "nominee_details");
    formData.append("type", isNew ? "Add" : "Edit");
    if (!isNew) {
      // Never sent before. The Edit branch requires it and does not fall back to
      // customerId, so an update without it is refused outright.
      formData.append("id", existingId);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/addDetailsWithImages`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      if (response.status === 201) {
        setNomineeDetailsData(response.data.savedData);
        console.log("Nominee Details updated successfully: ", response.data);
        setIsEditing(false);

        if (type === "Add" || (isEditing && mode === "businessLoan")) {
          handleNext();
        }
        if (isEditing && mode === "customer") {
          navigate("/addEditCustomer", {
            state: { type: "View", stepNo: 2, customerId },
          });
        }
        setErrorMsg("");
      }
    } catch (error) {
      console.error("Error updating Nominee Details: ", error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Error updating nominee details";
      setErrorMsg(msg);
    }
  };

  const onError = (errors) => {
    const errorMessages = Object.keys(errors).reduce((acc, key) => {
      acc[key] = errors[key].message;
      return acc;
    }, {});
    console.log("Form errors:", errorMessages);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(nomineeDetailsData); // Reset to original data
    setFilePreviews({}); // Clear any new file previews
  };

  return (
    <div className="bg-white rounded-xl border border-border-light shadow-sm overflow-hidden p-8 max-w-6xl mx-auto mt-6">
      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5.5 h-5.5">
            <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-ink-900">Nominee Details</h2>
          <p className="text-xs text-ink-500 mt-0.5">Enter nominee relationship and identity information</p>
        </div>
        {!locked && type === "View" && !isEditing && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleEditClick}
          >
            Edit Details
          </Button>
        )}
      </div>
      <ErrorMessage message={errorMsg} />
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-5 mb-8">
          {(mode === "customer"
            ? nomineeDetailsFields
            : memberNomineeDetailsFields
          ).map((field, index) => (
            <div
              key={index}
              className={field.name === "idProofFrontImage" ? "md:col-start-1 col-span-1" : "col-span-1"}
            >
              <FormInput
                label={field.label}
                type={field.type}
                name={field.name}
                register={register}
                watch={watch}
                control={control}
                errors={errors}
                options={field.options || []}
                readOnly={type === "View" && !isEditing}
                handleFileChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setValue(field.name, file, { shouldValidate: true });
                  }
                  handleFileChange(e, field.name, setFilePreviews);
                }}
                existingFileUrl={existingFiles[field.name] || ""}
                filePreview={filePreviews[field.name]?.previewUrl || ""}
                required={field.required}
                placeholder={field.placeholder}
              />
            </div>
          ))}
        </div>
        {(type !== "View" || isEditing) && (
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-border-light">
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                } else {
                  navigate("/customers");
                }
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {isEditing ? "Update" : "Save & Next"}
            </Button>
          </div>
        )}
        {type === "View" && !isEditing && mode === "customer" && (
          <div className="flex justify-center gap-3 mt-8 pt-6 border-t border-border-light">
            <Button
              variant="secondary"
              onClick={() =>
                navigate("/addEditCustomer", {
                  state: { type: "View", stepNo: 2, customerId },
                })
              }
            >
              Back
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};


export default NomineeDetails;